import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
@Injectable({
  providedIn: 'root',
})
export class GoogleTrackingService {
  private scriptLoaded = false;
  private routerSub?: Subscription;

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  // ---------- Public init ----------
  init(): void {
    if (!this.isBrowser()) return;
    if (!environment.production || !environment.googleAdsId) return;

    this.loadScriptOnce();
    this.startRouteTracking();
  }

  // ---------- Browser check ----------
  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  // ---------- Load gtag safely ----------
  private loadScriptOnce(): void {
    if (this.scriptLoaded) return;

    // Prepare dataLayer + gtag BEFORE script loads (prevents race condition)
    window.dataLayer = window.dataLayer || [];

    window.gtag = function (...args: any[]) {
      window.dataLayer.push(args);
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAdsId}`;

    script.onload = () => {
      window.gtag('js', new Date());
      window.gtag('config', environment.googleAdsId);
    };

    document.head.appendChild(script);

    this.scriptLoaded = true;
  }

  // ---------- Track SPA route changes ----------
  private startRouteTracking(): void {
    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        window.gtag('config', environment.googleAdsId, {
          page_path: event.urlAfterRedirects,
        });
      });
  }

  // ---------- Conversion tracking ----------
  trackConversion(label: string): void {
    if (!this.isBrowser()) return;
    if (!environment.production || !environment.googleAdsId) return;

    window.gtag('event', 'conversion', {
      send_to: `${environment.googleAdsId}/${label}`,
    });
  }

  trackEvent(eventName: string, params: any = {}): void {
    if (!this.isBrowser()) return;
    if (!environment.production || !environment.googleAnalyticsId) return;

    window.gtag('event', eventName, params);
  }

  // ---------- Cleanup ----------
  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }
}
